import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isString from 'lodash/isString';
import { Affix, Pagination, Table as AntTable } from 'antd';
import Separator from '../layout/Separator';
import './Table.css';

export default class Table extends React.Component {
  static propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    pagination: PropTypes.shape({
      current: PropTypes.number,
      total: PropTypes.number,
      pageSize: PropTypes.number,
      position: PropTypes.string,
    }),
    lazy: PropTypes.bool,
    selectedRows: PropTypes.arrayOf(PropTypes.string),
    onSelect: PropTypes.func,
    onChange: PropTypes.func,
    onSelectRows: PropTypes.func,
    fetching: PropTypes.bool,
    selectable: PropTypes.bool,
    expandableComponent: PropTypes.func,
    rowClassName: PropTypes.func,
    filteredValue: PropTypes.string,
    sortOrder: PropTypes.string,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    bordered: PropTypes.bool,
    showTableCount: PropTypes.bool,
    indentSize: PropTypes.number,
    locale: PropTypes.shape({
      filterConfirm: PropTypes.string,
      filterReset: PropTypes.string,
      emptyText: PropTypes.string,
    }),
    scroll: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
    }),
    getCheckboxProps: PropTypes.func,
    fixedHorizontalScroll: PropTypes.bool,
  };

  static defaultProps = {
    onSelect: null,
    onChange: null,
    onSelectRows: null,
    fetching: false,
    pagination: null,
    selectedRows: null,
    selectable: false,
    expandableComponent: null,
    rowClassName: () => null,
    filteredValue: null,
    sortOrder: null,
    bordered: true,
    showTableCount: true,
    indentSize: 10,
    rowKey: 'id',
    locale: {},
    scroll: {},
    lazy: false,
    getCheckboxProps: () => false,
    fixedHorizontalScroll: false,
  };

  constructor(props) {
    super(props);
    this.tableBodyRef = React.createRef();
    this.xScrollRef = React.createRef();
  }

  sort = (a, b, path) => {
    if (isString(get(a, path, ''))) {
      return get(a, path, '').localeCompare(get(b, path, ''));
    }
    return isNaN(a[path]) ? -9999999 : isNaN(b[path]) ? 9999999 : a[path] - b[path];
  };

  isToFixHorizontalScroll = () => {
    return (
      !!this.props.scroll &&
      !!this.props.scroll.x &&
      this.props.fixedHorizontalScroll &&
      !!this.props.pagination &&
      !!this.props.pagination.total &&
      !!this.tableBodyRef.current
    );
  };

  handlePageChange = (current) => {
    this.props.onChange({ current });
  };

  handleHorizontalScrollChange = (event) => {
    this.tableBodyRef.current.scrollLeft = event.target.scrollLeft;
  };

  render() {
    const isToFixHorizontalScroll = this.isToFixHorizontalScroll();

    if (isToFixHorizontalScroll) {
      const timeout = setTimeout(() => {
        if (this.xScrollRef.current) {
          this.xScrollRef.current.style.width = `${this.tableBodyRef.current.scrollWidth}px`;
        }
        clearTimeout(timeout);
      }, 100);
    }

    return (
      <div
        ref={(node) => {
          if (node) {
            this.tableBodyRef.current = node.querySelector('.ant-table-body');
          }
        }}
        className={isToFixHorizontalScroll ? 'ant-table-wrapper__xScrollFixed' : null}
      >
        <AntTable
          rowClassName={this.props.rowClassName}
          pagination={false}
          rowKey={this.props.rowKey}
          rowSelection={
            this.props.onSelectRows && {
              hideDefaultSelections: true,
              selectedRowKeys: this.props.selectedRows,
              onChange: this.props.onSelectRows,
              getCheckboxProps: this.props.getCheckboxProps,
            }
          }
          bordered={this.props.bordered}
          indentSize={this.props.indentSize}
          locale={{
            filterConfirm: 'Ok',
            filterReset: 'Reset',
            emptyText: 'No Data',
            ...this.props.locale,
          }}
          dataSource={this.props.rows}
          columns={this.props.columns.map((column) => ({
            ...column,
            title: column.label,
            width: column.width,
            dataIndex: column.path,
            render: column.render,
            sorter: column.sort
              ? this.props.lazy
                ? true
                : (a, b) => (typeof column.sort === 'function' ? column.sort(a, b) : this.sort(a, b, column.path))
              : null,
            defaultSortOrder: column.defaultSortOrder,
            className: `${column.className || ''} ${this.props.selectable ? 'selectable-table' : ''}`,
            onCellClick: this.props.selectable && column.path !== 'action' ? (row) => this.props.onSelect(row) : null,
            align: column.align,
            fixed: column.fixed,
          }))}
          onChange={this.props.onChange}
          loading={this.props.fetching}
          expandedRowRender={this.props.expandableComponent}
          filteredValue={this.props.filteredValue}
          sortOrder={this.props.sortOrder}
          size="small"
          scroll={this.props.scroll}
        />
        {isToFixHorizontalScroll && (
          <Affix offsetBottom={0}>
            <div style={{ overflowX: 'auto' }} onScroll={this.handleHorizontalScrollChange}>
              <div
                style={{
                  height: 1,
                }}
                ref={this.xScrollRef}
              />
            </div>
          </Affix>
        )}
        <Separator size="xs" />
        {!!this.props.pagination && !!this.props.pagination.total && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              size="small"
              total={this.props.pagination.total}
              current={this.props.pagination.current}
              pageSize={this.props.pagination.pageSize}
              showTotal={(total) => `Total ${total} items`}
              onChange={this.handlePageChange}
            />
          </div>
        )}
        {!this.props.pagination && this.props.showTableCount && (
          <small>{this.props.rows.length} registers found.</small>
        )}
      </div>
    );
  }
}
